<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import { mapGetters } from 'vuex';
import {
  computeStepVariablesMap,
  insertStepAt,
} from '../../helpers/automationUtils';
import AddStepButton from '../addStepDropdown/AddStepButton.vue';
import triggerValidationMixins from './helpers/triggerValidationMixins';
import Step from './step/Index.vue';

export default {
  components: {
    Step,
    AddStepButton,
  },
  mixins: [triggerValidationMixins],
  props: {
    automation: {
      type: Object,
      default: () => {},
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['stepChange', 'stepDelete'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  computed: {
    ...mapGetters({
      actionsRegistry: 'wgptAutomationRegistry/getActions',
    }),
    trigger() {
      return this.automation.trigger || {};
    },
    steps() {
      return this.automation.steps?.v || [];
    },
    hasTrigger() {
      return !!this.trigger.type;
    },
    triggerRegistry() {
      return this.$store.getters['wgptAutomationRegistry/getTrigger'](
        this.trigger.type
      );
    },
    stepVariablesMap() {
      return computeStepVariablesMap(
        this.steps,
        this.actionsRegistry,
        this.variables
      );
    },
    routerLinkTo() {
      if (this.v$.trigger.$error) {
        return { query: { dirty: true } };
      }

      return { query: {} };
    },
  },
  methods: {
    onStepChange(...args) {
      this.$emit('stepChange', ...args);
    },
    onStepDelete(...step) {
      this.$emit('stepDelete', ...step);
    },
    addStepAt(index, newStep) {
      this.$emit('stepChange', null, insertStepAt(this.steps, index, newStep));
    },
  },
};
</script>

<template>
  <div class="trigger mt-8">
    <router-link
      :to="routerLinkTo"
      :class="{
        'step-active': !$route.query.selected,
      }"
      class="pointer-events-none"
      replace
    >
      <div class="trigger-content" :class="{ error: v$.trigger.$error }">
        <div class="py-2 px-3 border-b border-n-strong font-semibold text-sm">
          <span class="text-base font-medium text-n-slate-12">
            {{ $t('WGPT_AUTOMATIONS.EDITOR.TRIGGER.LABEL') }}
          </span>
        </div>
        <div class="py-2 px-3">
          <p class="text-xs text-n-slate-11 m-0">
            <span
              v-if="!hasTrigger"
              class="m-0 italic text-slate-400 dark:text-slate-400"
            >
              {{ $t('WGPT_AUTOMATIONS.EDITOR.TRIGGER.PLACEHOLDER') }}
            </span>
            <span v-else-if="triggerRegistry">
              {{ toLocale(triggerRegistry.label) }}
            </span>
            <span v-else class="space-y-2 animate-loader-pulse">
              <div class="bg-slate-100 dark:bg-slate-800/50 rounded w-1/2">
                &nbsp;
              </div>
            </span>
          </p>
        </div>
      </div>
    </router-link>

    <div v-if="!disabled && steps.length === 0" class="connector-bottom">
      <AddStepButton
        class="mt-4"
        :disabled="!hasTrigger"
        @add-step="e => addStepAt(0, e)"
        @delete-step="onStepDelete"
      />
    </div>

    <div class="steps">
      <Step
        v-for="(step, index) of steps"
        :key="step.id"
        :step="step"
        :trigger="trigger"
        :first="index === 0"
        :last="index === steps.length - 1"
        :variables="stepVariablesMap.get(step.id) || []"
        :disabled="disabled"
        @add-step-before="e => addStepAt(index, e)"
        @add-step-after="e => addStepAt(index + 1, e)"
        @step-change="onStepChange"
        @step-delete="onStepDelete"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trigger-content {
  @apply rounded-xl shadow-sm border border-n-strong bg-n-solid-2 dark:bg-n-solid-3;
  @apply min-w-[200px] w-max mx-auto break-all cursor-pointer pointer-events-auto;
}

.step-active > .trigger-content {
  @apply outline-none;
  outline-color: var(--triggerThemeColor);
  outline-offset: 1px;
}

:not(.step-active) > .trigger-content.error {
  @apply outline-none outline-n-ruby-8;
  outline-offset: 1px;
}

[dir='ltr'] {
  .connector-top,
  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-r-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(50%);
  }
}
[dir='rtl'] {
  .connector-top,
  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(-50%);
  }
}
</style>
